import {observer} from 'mobx-react';
import React from 'react';
import {PageHeading, PromiseMessage, PromiseRetry, TimeZoneSelect} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {UserViewRender} from '../view-render/UserViewRender';
import {CurrentUserSceneStore} from './CurrentUserSceneStore';

function* updateTimeZoneControls(sceneStore: CurrentUserSceneStore): ReactNodeGenerator {
  if (sceneStore.mode !== 'update-timezone')
    return;

  const timeZonesState = sceneStore.timeZonesState;

  yield (<PromiseMessage key="timezone-loader"
                         label="Loading time zones" state={timeZonesState} align="center"/>);

  yield (<PromiseRetry key="timezone-retry"
                       state={timeZonesState} retry={sceneStore.loadTimeZones} align="center"/>);

  if (timeZonesState?.isResolved !== true)
    return;

  const store = sceneStore.updateTimeZone!;
  const timeZone = store.timeZone;
  const state = store.save;
  const isPending = state?.isPending === true;

  yield (
    <div className="row" key="set-time-zone-controls">
      <div className="col-md-4 col-xs-6">
        <div className={timeZone.formGroupClassName}>
          <label>Time zone</label>
          <TimeZoneSelect options={store.options}
                          value={timeZone.value}
                          onChange={timeZone.setValue}
                          onBlur={timeZone.markTouched}/>
          <div className="invalid-text">Enter time zone</div>
        </div>
      </div>
      <div className="col-auto ml-auto">
        <button type="button" className="btn btn-primary"
                onClick={sceneStore.completeUpdateTimeZone}
                disabled={isPending}>
          Update
        </button>

        <button type="button" className="btn btn-primary"
                onClick={sceneStore.cancel}
                disabled={isPending}>
          Cancel
        </button>
      </div>
    </div>);

  yield (<PromiseMessage key="save"
                         label="Updating time zone" state={state} align="right"/>);
}

function* updatePasswordControls(sceneStore: CurrentUserSceneStore): ReactNodeGenerator {
  if (sceneStore.mode !== 'update-password')
    return;

  const store = sceneStore.updatePassword!;
  const {oldPassword, newPassword, repeatNewPassword} = store;
  const state = store.save;
  const isPending = state?.isPending === true;

  yield (
    <div className="row" key="update-password-controls">
      <div className="col-md-4 col-xs-6">
        <div className={oldPassword.formGroupClassName}>
          <label>Current password </label>
          <input type="password" className="form-control"
                 value={oldPassword.value}
                 onChange={oldPassword.onChangeHandler}
                 onBlur={oldPassword.markTouched}/>
          <div className="invalid-text">Enter current password</div>
        </div>
      </div>
      <div className="col-md-4 col-xs-6">
        <div className={newPassword.formGroupClassName}>
          <label>New password </label>
          <input type="password" className="form-control"
                 value={newPassword.value}
                 onChange={newPassword.onChangeHandler}
                 onBlur={newPassword.markTouched}/>
          <div className="invalid-text">Enter new password</div>
        </div>
      </div>
      <div className="col-md-4 col-xs-6">
        <div className={store.repeatNewPasswordFgName}>
          <label>Repeat new password</label>
          <input type="password" className="form-control"
                 value={repeatNewPassword.value}
                 onChange={repeatNewPassword.onChangeHandler}
                 onBlur={repeatNewPassword.markTouched}/>
          <div className="invalid-text">Repeat new password</div>
        </div>
      </div>
      <div className="col-auto ml-auto no-wrap">
        <button type="button" className="btn btn-primary"
                onClick={sceneStore.completeUpdatePassword}
                disabled={isPending}>
          Update
        </button>
        <button type="button" className="btn btn-primary"
                onClick={sceneStore.cancel}
                disabled={isPending}>
          Cancel
        </button>
      </div>
    </div>);

  yield (<PromiseMessage key="save"
                         label="Updating password" state={state} align="right"/>);
}

function* renderer(store: CurrentUserSceneStore): ReactNodeGenerator {
  const user = store.user;
  if (user === null)
    return;

  yield (<PageHeading key="heading">Current user</PageHeading>);

  yield (
    <div className="row" key="current-user">
      <div className="col">
        <div className="card">
          <div className="card-header">
            Current user
          </div>
          <div className="card-body">
            <UserViewRender user={user} adminView={false}/>

            {
              store.mode === 'view' &&
              <div className="row">
                <div className="col-auto ml-auto">
                  <button type="button" className="btn btn-primary"
                          onClick={store.beginUpdateTimeZone}>
                    <span className="fas fa-user-clock"/> Update time zone
                  </button>
                  <button type="button" className="btn btn-primary"
                          onClick={store.beginUpdatePassword}>
                    <span className="fas fa-key"/> Update password
                  </button>
                </div>
              </div>
            }

            {Array.from(updateTimeZoneControls(store))}

            {Array.from(updatePasswordControls(store))}
          </div>
        </div>
      </div>
    </div>);
}

@observer
export class CurrentUserScene extends React.Component {
  store: CurrentUserSceneStore;

  constructor(props: any) {
    super(props);
    this.store = new CurrentUserSceneStore();
  }

  render() {
    return Array.from(renderer(this.store));
  }

  componentWillUnmount() {
    this.store.dispose();
  }
}
